'use client';

import type { CSSProperties } from 'react';
import { Avatar, Link } from '@mokoboko/components';
import { ChevronRight } from 'lucide-react';

import { useElementHeight } from '@/hooks/use-element-height';
import { buildInstructorName, getFullName } from '@/lib/utils';
import type { AdminUser } from '@/state/admin-users';

type SectionInstructor = Pick<AdminUser, 'first_name' | 'profile_picture'> & {
  last_name?: string | null;
};

// From sm the photo stands beside the heading and the text, as tall as them
// down to the link, within the min and max its classes set (the max stops text
// width being traded for photo height in a loop); a phone keeps an 80px photo
// beside the text under a full-width heading. CSS cannot size a square from a
// sibling, so that column is measured.
export const InstructorSection = ({
  instructor,
  bio,
  onOpen,
}: {
  instructor: SectionInstructor;
  bio: string;
  onOpen: () => void;
}) => {
  const [measureText, textHeight] = useElementHeight();
  const photoSize = {
    '--photo-size': `${textHeight}px`,
  } as CSSProperties;

  return (
    <section
      style={photoSize}
      className="flex min-w-0 items-start gap-4 sm:gap-8"
    >
      <div ref={measureText} className="flex min-w-0 flex-1 flex-col gap-4">
        <h2 className="heading-section [overflow-wrap:anywhere]">
          {buildInstructorName(instructor.first_name, instructor.last_name)}
        </h2>
        <div className="flex items-start gap-4">
          <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
            <p className="line-clamp-3 text-sm text-gray-700 [overflow-wrap:anywhere]">
              {bio}
            </p>
            <Link
              size="sm"
              iconRight={<ChevronRight size={16} aria-hidden />}
              onClick={onOpen}
            >
              More about {buildInstructorName(instructor.first_name)}
            </Link>
          </div>
          {/* Initials on a grey block read as a missing photo, so the text
              takes the row instead. */}
          {instructor.profile_picture && (
            <Avatar
              shape="rounded"
              size="2xl"
              className="size-20 shrink-0 sm:hidden"
              src={instructor.profile_picture}
              name={getFullName(instructor.first_name, instructor.last_name)}
            />
          )}
        </div>
      </div>
      {instructor.profile_picture && (
        <Avatar
          shape="rounded"
          size="2xl"
          className="hidden shrink-0 sm:inline-flex sm:size-[var(--photo-size)] sm:max-h-40 sm:min-h-[120px] sm:min-w-[120px] sm:max-w-40"
          src={instructor.profile_picture}
          name={getFullName(instructor.first_name, instructor.last_name)}
        />
      )}
    </section>
  );
};
